import React, { useCallback, useEffect, useState } from 'react';
import Collaborator from 'Components/Collaborator/Collaborator';
import { getNameInitials } from 'helpers';
import Modal from 'react-bootstrap/Modal';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { toast } from 'react-toastify';
import Styles from './BrandShareModal.module.scss';
import { FiUsers, FiUserPlus } from 'react-icons/fi';
import { getAllMembers } from 'services/teamService';
import {
  getBrandCollaborators,
  addBrandCollaborator,
  removeBrandCollaborator,
} from 'services/brandSharingService';

const selectStyles = {
  option: (styles, { isSelected, isFocused }) => {
    return {
      ...styles,
      padding: '10px 14px',
      fontSize: '14px',
      fontWeight: '500',
      fontFamily: "'Inter', sans-serif",
      color: isSelected ? '#00a7b7' : '#344054',
      backgroundColor: isFocused ? '#F9FAFB' : '#FFFFFF',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      borderLeft: isSelected ? '3px solid #00a7b7' : '3px solid transparent',

      '&:active': {
        backgroundColor: '#F2F4F7',
      },
    };
  },

  control: (baseStyles, { isFocused }) => {
    return {
      ...baseStyles,
      minHeight: '44px',
      borderRadius: '8px',
      border: `1px solid ${isFocused ? '#00a7b7' : '#D0D5DD'}`,
      boxShadow: isFocused ? '0 0 0 3px rgba(0, 167, 183, 0.1)' : 'none',
      cursor: 'pointer',
      fontFamily: "'Inter', sans-serif",
      fontSize: '14px',
      transition: 'all 0.2s ease',

      '&:hover': {
        borderColor: '#00a7b7',
      },
    };
  },

  menu: (baseStyles) => {
    return {
      ...baseStyles,
      borderRadius: '8px',
      boxShadow:
        '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
      border: '1px solid #E4E7EC',
      overflow: 'hidden',
      marginTop: '4px',
    };
  },

  menuList: (baseStyles) => {
    return {
      ...baseStyles,
      padding: '4px',
    };
  },

  singleValue: (baseStyles) => {
    return {
      ...baseStyles,
      color: '#344054',
      fontWeight: '500',
      fontFamily: "'Inter', sans-serif",
    };
  },

  placeholder: (baseStyles) => {
    return {
      ...baseStyles,
      color: '#98A2B3',
      fontFamily: "'Inter', sans-serif",
    };
  },

  dropdownIndicator: (baseStyles, { isFocused }) => {
    return {
      ...baseStyles,
      color: isFocused ? '#00a7b7' : '#667085',
      transition: 'all 0.2s ease',

      '&:hover': {
        color: '#00a7b7',
      },
    };
  },

  indicatorSeparator: () => {
    return {
      display: 'none',
    };
  },
};

const BrandShareModal = ({ brand, ...props }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [brandOwner, setBrandOwner] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);

  // Use business_id for API calls (fallback to id for backwards compatibility)
  const businessId = brand?.business_id || brand?.id;

  const fetchTeamMembers = useCallback(async () => {
    try {
      const response = await getAllMembers();
      const { data } = response || {};
      // Backend returns array directly, not wrapped in team_collaborators
      const team_collaborators = Array.isArray(data) ? data : (data?.team_collaborators || []);

      // Filter out users who are already collaborators or the owner
      const members = (team_collaborators || []).map((member) => ({
        value: member.id,
        label: member.name || member.email,
        email: member.email,
        avatar: member.medium_avatar,
        userId: member.id,
      }));

      setTeamMembers(members);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  }, []);

  const fetchCollaborators = useCallback(async () => {
    if (!businessId) return;

    try {
      const response = await getBrandCollaborators(businessId);
      const { data } = response || {};
      const { owner, collaborators: collabs } = data || {};

      setBrandOwner(owner);
      setCollaborators(collabs || []);
    } catch (error) {
      console.error('Failed to fetch collaborators:', error);
    }
  }, [businessId]);

  useEffect(() => {
    if (props.show && businessId) {
      fetchTeamMembers();
      fetchCollaborators();
    }
  }, [props.show, businessId, fetchTeamMembers, fetchCollaborators]);

  const handleAddCollaborator = async () => {
    if (!selectedMember) {
      toast.warning('Please select a team member');
      return;
    }

    if (selectedMember.userId === brandOwner?.id) {
      toast.warning('This user is already the owner of this brand');
      return;
    }

    // Check if already a collaborator
    const alreadyAdded = collaborators.some(
      (collab) => collab.user.id === selectedMember.userId
    );
    if (alreadyAdded) {
      toast.warning('This team member already has access to this brand');
      return;
    }

    // Optimistically add the collaborator to UI immediately BEFORE any async work
    const newCollaborator = {
      id: `temp-${Date.now()}`,
      user: {
        id: selectedMember.userId,
        name: selectedMember.label,
        email: selectedMember.email,
        avatar: selectedMember.avatar,
      },
      created_at: new Date().toISOString(),
    };
    const selectedMemberCopy = selectedMember;

    // Update UI immediately
    setCollaborators((prev) => {
      console.log('Previous collaborators:', prev);
      console.log('Adding new collaborator:', newCollaborator);
      return [...prev, newCollaborator];
    });
    setSelectedMember(null);
    setIsLoading(true);

    try {
      const response = await addBrandCollaborator(businessId, selectedMemberCopy.userId);

      if (response.status === 201) {
        toast.success('Team member added successfully!');
        // Refresh to get the real data in background
        fetchCollaborators();
      }
    } catch (error) {
      // Revert on error
      setCollaborators((prev) => prev.filter((c) => c.id !== newCollaborator.id));
      const errorMessage =
        error?.response?.data?.error || 'Failed to add team member';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveCollaborator = async (businessUserId) => {
    if (!window.confirm('Remove this collaborator from the brand?')) return;

    try {
      const response = await removeBrandCollaborator(businessId, businessUserId);
      if (response.status === 200) {
        toast.success('Collaborator removed successfully');
        fetchCollaborators();
      }
    } catch (error) {
      toast.error('Failed to remove collaborator');
    }
  };

  const canShareBrand = () => {
    const planName = user?.subscriptions?.current_subscription?.plan_name;
    return planName === 'Business Plan' || planName === 'AppSumo Tier3' || planName === 'Lifetime Business Plan';
  };

  // Filter team members to exclude current user, owner, and existing collaborators
  const availableMembers = teamMembers.filter((member) => {
    const isCurrentUser = member.userId === user?.id;
    const isOwner = member.userId === brandOwner?.id;
    const isCollaborator = collaborators.some(
      (collab) => collab.user.id === member.userId
    );
    return !isCurrentUser && !isOwner && !isCollaborator;
  });

  return (
    <Modal
      {...props}
      size="md"
      aria-labelledby="brand-share-modal"
      centered
      dialogClassName={Styles.shareModal}
      style={{ zIndex: 10000 }}
    >
      <div className={Styles.modalHeader}>
        <div className={Styles.headerIcon}>
          <FiUsers size={20} />
        </div>
        <h2>Share Brand: {brand?.brand_name || 'Untitled'}</h2>
      </div>

      <Modal.Body className={Styles.modalBody}>
        {canShareBrand() ? (
          <>
            {/* Add Team Member Section */}
            <div className={Styles.addMemberSection}>
              <label className={Styles.label}>Add team member</label>
              <div className={Styles.inputWrapper}>
                <div className={Styles.selectWrapper}>
                  <Select
                    isSearchable={true}
                    styles={selectStyles}
                    options={availableMembers}
                    placeholder="Select a team member..."
                    value={selectedMember}
                    onChange={setSelectedMember}
                    isDisabled={isLoading}
                    noOptionsMessage={() =>
                      availableMembers.length === 0
                        ? 'All team members already have access'
                        : 'No team members found'
                    }
                  />
                </div>
                <button
                  className={Styles.addButton}
                  onClick={handleAddCollaborator}
                  disabled={!selectedMember || isLoading}
                >
                  <FiUserPlus size={16} />
                  {isLoading ? 'Adding...' : 'Add'}
                </button>
              </div>
              <p className={Styles.helperText}>
                Only team members can be added to shared brands
              </p>
            </div>

            {/* Collaborators List Section */}
            <div className={Styles.collaboratorsSection}>
              <h3 className={Styles.sectionTitle}>People with access</h3>
              <div className={Styles.collaboratorsList}>
                {/* Owner */}
                {brandOwner && (
                  <div className={Styles.collaboratorItem}>
                    <div className={Styles.collaboratorInfo}>
                      {brandOwner.avatar ? (
                        <img
                          src={brandOwner.avatar}
                          alt={brandOwner.name}
                          className={Styles.avatar}
                        />
                      ) : (
                        <div className={Styles.avatarPlaceholder}>
                          {getNameInitials(brandOwner.name)}
                        </div>
                      )}
                      <div className={Styles.collaboratorDetails}>
                        <p className={Styles.collaboratorName}>
                          {brandOwner.name}
                          {brandOwner.id === user.id && ' (You)'}
                        </p>
                        <p className={Styles.collaboratorEmail}>
                          {brandOwner.email}
                        </p>
                      </div>
                    </div>
                    <div className={Styles.ownerBadge}>Owner</div>
                  </div>
                )}

                {/* Collaborators */}
                {collaborators.map((collab) => {
                  const { id, user: collabUser, created_at } = collab;
                  const { name, email, avatar } = collabUser || {};

                  return (
                    <div key={id} className={Styles.collaboratorItem}>
                      <div className={Styles.collaboratorInfo}>
                        {avatar ? (
                          <img
                            src={avatar}
                            alt={name}
                            className={Styles.avatar}
                          />
                        ) : (
                          <div className={Styles.avatarPlaceholder}>
                            {getNameInitials(name)}
                          </div>
                        )}
                        <div className={Styles.collaboratorDetails}>
                          <p className={Styles.collaboratorName}>{name}</p>
                          <p className={Styles.collaboratorEmail}>{email}</p>
                        </div>
                      </div>
                      {brandOwner?.id === user.id && (
                        <button
                          className={Styles.removeButton}
                          onClick={() => handleRemoveCollaborator(id)}
                          title="Remove collaborator"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}

                {collaborators.length === 0 && (
                  <div className={Styles.emptyState}>
                    <p>No collaborators yet</p>
                    <span>Add team members to start collaborating</span>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className={Styles.upgradeBanner}>
            <div className={Styles.bannerIcon}>
              <FiUsers size={24} />
            </div>
            <div className={Styles.bannerContent}>
              <p className={Styles.bannerTitle}>Brand Sharing</p>
              <p className={Styles.bannerDescription}>
                Upgrade to Business Plan or AppSumo Tier 3 to share brands with your team
              </p>
            </div>
            <button
              className={Styles.upgradeButton}
              onClick={() => {
                props.onHide();
                // Navigate to pricing page
                window.location.href = '/plan';
              }}
            >
              Upgrade Now
            </button>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className={Styles.modalFooter}>
        <button className={Styles.doneButton} onClick={props.onHide}>
          Done
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default BrandShareModal;
